import {
  SubscriptionCard,
  SubscriptionCardSkeleton,
} from '@/components/subscription-card';
import { TopupListCard } from '@/components/topup-list-card';
import { FaqSection } from '@/components/faq-section';
import { IllustratedEmptyState } from '@/components/illustrated-empty-state';
import { getAllSubscriptions } from '@/helpers/subscription';
import { AppearanceSettings } from '@/state/appearance-settings';
import useFaqs from '@/state/faqs';
import useSubscriptionGroups from '@/state/subscription-groups';
import useTopup from '@/state/topup-content';
import { useAuth } from '@/contexts/auth';
import { Separator } from '@mokoboko/components';

const EMPTY_COPY = {
  all: {
    title: 'No memberships found',
    description: (
      <>
        Once memberships are available,
        <br className="sm:hidden" /> they will appear here
      </>
    ),
  },
  forClass: {
    title: 'No memberships for this class',
    description: 'No membership includes this class right now.',
  },
};

export const Subscriptions = ({
  subscriptionSettings,
  // A section of the home page, but the whole of /subscriptions — only the
  // caller knows which, and on /subscriptions nothing else supplies the h1.
  headingAs: Heading = 'h2',
  showHeading = true,
  showFaq = true,
  classId,
}: {
  subscriptionSettings: AppearanceSettings['subscription'];
  headingAs?: 'h1' | 'h2';
  showHeading?: boolean;
  showFaq?: boolean;
  classId?: string;
}) => {
  const { user } = useAuth();
  const { data: subscriptionGroups, isLoading: isSubscriptionGroupsLoading } =
    useSubscriptionGroups({ include_subscriptions: true, class_id: classId });
  const { data: topup } = useTopup({ enabled: !!user });
  const { data: faqs } = useFaqs({
    entityType: 'SUBSCRIPTION',
    enabled: showFaq,
  });

  // Fixed copy, so the loading state draws it too.
  const heading = showHeading && (
    <div className="flex flex-col gap-1">
      <Heading className="heading-section">Memberships</Heading>
      <p className="text-sm text-gray-700">Regular practice, better value</p>
    </div>
  );

  if (isSubscriptionGroupsLoading) {
    return (
      <div className="flex w-full flex-col gap-4 sm:gap-5">
        {heading}
        <SubscriptionCardSkeleton />
        <SubscriptionCardSkeleton />
        <SubscriptionCardSkeleton />
      </div>
    );
  }

  const allSubscriptions = getAllSubscriptions(subscriptionGroups ?? []);

  if (!allSubscriptions.length) {
    return (
      <IllustratedEmptyState
        illustration="subscription-3d"
        {...EMPTY_COPY[classId ? 'forClass' : 'all']}
        className="py-6"
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-5">
      {heading}

      {user && topup && <TopupListCard topup={topup} />}

      {allSubscriptions.map((subscription) => (
        <SubscriptionCard
          key={subscription.subscription_id}
          subscription={subscription}
          subscriptionSettings={subscriptionSettings}
        />
      ))}

      {showFaq && faqs.length > 0 && (
        <>
          <Separator className="my-3" />
          <FaqSection faqs={faqs} className="max-sm:pt-0" />
        </>
      )}
    </div>
  );
};
